import axios from 'axios';

/**
 * Pull the most user-meaningful message out of an unknown error. Order:
 *   1. FastAPI's `detail` string (single error)
 *   2. FastAPI's `detail[0].msg` (validation error array)
 *   3. Backend's `message` if any
 *   4. A polished fallback based on HTTP status — NOT `err.message`,
 *      which Axios sets to "Request failed with status code 403" / etc.
 *      That string is unhelpful for end users and shows up nowhere in
 *      the design system; we replace it with a status-aware human
 *      sentence ("This feature needs a verified email", "Not found", etc).
 *   5. Final fallback: "Something went wrong"
 *
 * The Axios default message is so ugly that surfacing it once is one of
 * the most common "looks unprofessional" bug reports — see the user's
 * 2026-05-20 ticket. Keep the polish here so every call site benefits.
 */
export const extractErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    const detail = (data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && (detail[0] as { msg?: unknown } | undefined)?.msg) {
      return String((detail[0] as { msg?: unknown }).msg);
    }
    const message = (data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string') return message;
    return _humanizeAxiosStatus(err);
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
};

/**
 * Map common HTTP statuses to a short, user-facing sentence. Avoids
 * dumping "Request failed with status code N" anywhere in the UI.
 */
const _humanizeAxiosStatus = (err: import('axios').AxiosError): string => {
  const status = err.response?.status ?? 0;
  if (status === 0) return 'Network unavailable. Check your connection.';
  if (status === 400) return 'That request was invalid. Try again.';
  if (status === 401) return 'You need to sign in again.';
  if (status === 403) return 'You don’t have permission for that action.';
  if (status === 404) return 'Not found.';
  if (status === 409) return 'That conflicts with the current state.';
  if (status === 422) return 'Some fields are missing or invalid.';
  if (status === 429) return 'Too many requests — wait a moment and retry.';
  if (status === 503) return 'Service temporarily unavailable.';
  if (status === 504) return 'Server took too long to respond.';
  if (status >= 500) return 'Something went wrong on our end. Try again.';
  if (status >= 400) return 'Request couldn’t be completed.';
  return 'Something went wrong';
};

/**
 * Should the caller silently swallow this error?
 *
 * Use case: a list-fetch returning 404/empty/forbidden isn't a USER error
 * — it's a "no data yet" state. The UI should render an empty state, not
 * pop a red toast. Call sites that want this opt-in: `if (isEmptyDataError(err)) return [];`
 *
 * What counts as "empty data" for our purposes:
 *   - 404 (resource doesn't exist yet — a fresh user with no agents)
 *   - 403 (verification gate or row-level access; treat as "no data
 *     visible" rather than "error" — the user's other surfaces will
 *     show the verification prompt where it belongs)
 *   - Network failures DO NOT count — those are real errors.
 *
 * This is opt-in. Mutation errors (POST/PUT/DELETE) must always surface;
 * never wrap a mutation in this helper.
 */
export const isEmptyDataError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status ?? 0;
  return status === 403 || status === 404;
};
